// Stripe Payment Processing Integration
// All PaymentIntents and rent payment calls go to landlordops-backend:
// POST /api/frontend-stripe/create-payment-intent, /confirm-payment, …
// POST /api/payments/rent/:id/pay, /create-ach-intent, …
// Never the CRA `server.js` mock - production must set REACT_APP_API_URL to that API.
import { loadStripe } from "@stripe/stripe-js";
import { getPaymentApiUrl } from "../api/config";

// Environment variables needed:
// REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_... or pk_live_...
// REACT_APP_API_URL=https://your-backend-api.com/api

export const stripeConfig = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
};

// Validate configuration
if (!stripeConfig.publishableKey) {
  console.error("REACT_APP_STRIPE_PUBLISHABLE_KEY is required");
}

// Initialize Stripe
export const initializeStripe = async () => {
  try {
    return await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  } catch (error) {
    console.error("Error initializing Stripe:", error);
    return null;
  }
};

// Get auth token for API calls (optional - returns null if no user)
const getAuthToken = async (required = true) => {
  try {
    // Import Firebase auth directly
    const { firebaseAuth } = await import("../../helpers/authentication");

    // Check if firebaseAuth is properly initialized
    if (!firebaseAuth) {
      if (required) {
        console.error("Firebase auth not initialized");
        throw new Error("Firebase authentication not available");
      }
      return null;
    }

    const user = firebaseAuth.currentUser;
    if (!user) {
      if (required) {
        console.error("No authenticated user found");
        throw new Error("User not authenticated. Please log in to continue.");
      }
      return null;
    }

    const token = await user.getIdToken();
    if (!token) {
      if (required) {
        throw new Error("Failed to get authentication token");
      }
      return null;
    }

    return token;
  } catch (error) {
    if (required) {
      console.error("Error getting auth token:", error);
      throw new Error("Failed to get authentication token");
    }
    return null;
  }
};

// Create payment intent for rent payment
// NOTE: This function calls the backend API to create PaymentIntents.
// The backend automatically sets all required metadata (app: "landlordops", rentPaymentId, etc.)
// DO NOT create PaymentIntents directly using Stripe.js - always use backend endpoints.
// See PAYMENT_INTENT_METADATA_REQUIREMENTS.md for details.
export const createRentPaymentIntent = async (
  amount,
  currency = "usd",
  metadata = {}
) => {
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/create-payment-intent`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ amount, currency, metadata }),
      }
    );

    if (!response.ok) {
      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        throw new Error(
          "Backend API endpoint not available. Please implement the backend API endpoints."
        );
      }

      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create payment intent");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating payment intent:", error);

    // If it's a network error or backend not available, provide a helpful message
    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("Backend API endpoint not available")
    ) {
      throw new Error(
        "Payment processing is not yet configured. Please contact support or implement the backend API endpoints."
      );
    }

    throw error;
  }
};

// Process rent payment
export const processRentPayment = async (
  paymentMethodId,
  paymentIntentId,
  amount,
  metadata
) => {
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken();
    const response = await fetch(`${apiBase}/frontend-stripe/confirm-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        paymentMethodId,
        paymentIntentId,
        amount,
        metadata,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to process payment");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error processing payment:", error);
    throw error;
  }
};

// New unified pay endpoints (backend finalized)
export const payRentPaymentCard = async ({
  rentPaymentId,
  paymentMethodId,
}) => {
  try {
    const apiBase = getPaymentApiUrl();
    // Tenant payments don't require authentication
    const authToken = await getAuthToken(false);
    const url = `${apiBase}/payments/rent/${encodeURIComponent(
      rentPaymentId
    )}/pay`;
    console.log("[payRentPaymentCard] Making request to:", url);
    console.log("[payRentPaymentCard] Request body:", { paymentMethodId });

    const headers = {
      "Content-Type": "application/json",
    };
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ paymentMethodId }),
    });

    console.log(
      "[payRentPaymentCard] Response status:",
      response.status,
      response.statusText
    );

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    console.log("[payRentPaymentCard] Response payload:", payload);

    if (!response.ok) {
      // Handle specific error codes
      let errorMessage = "Payment failed";

      if (typeof payload === "object" && payload) {
        errorMessage = payload.message || payload.error || errorMessage;
      } else if (typeof payload === "string") {
        errorMessage = payload.slice(0, 200);
      }

      // Map HTTP status codes to user-friendly messages
      switch (response.status) {
        case 401:
          errorMessage = "Authentication failed. Please log in again.";
          break;
        case 404:
          errorMessage = "Payment not found. Please contact support.";
          break;
        case 409:
          errorMessage = "This payment has already been processed.";
          break;
        case 422:
          errorMessage = "Invalid payment method. Please try a different card.";
          break;
        case 500:
          errorMessage = "Server error. Please try again later.";
          break;
      }

      throw new Error(errorMessage);
    }

    // Return the response payload (should have status, stripePaymentIntentId, rentPaymentId)
    return payload;
  } catch (error) {
    console.error("[payRentPaymentCard] Error:", error);
    throw error;
  }
};

// Create PaymentIntent for ACH. Prefer Payment Element (omit bankAccountToken); optional bankAccountToken for saved PM.
// With no token: backend uses Instant Verification (Financial Connections); frontend confirms with Payment Element.
export const createRentPaymentIntentACH = async ({
  rentPaymentId,
  bankAccountToken,
}) => {
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken(false);
    const url = `${apiBase}/payments/rent/${encodeURIComponent(
      rentPaymentId
    )}/create-ach-intent`;
    const body = bankAccountToken ? { bankAccountToken } : {};

    const headers = { "Content-Type": "application/json" };
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    console.log(
      "[createRentPaymentIntentACH] Response status:",
      response.status,
      response.statusText
    );

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    console.log("[createRentPaymentIntentACH] Response payload:", payload);

    if (!response.ok) {
      // Handle specific error codes
      let errorMessage = "Failed to create payment intent";

      // Check if response is HTML (404 page from backend)
      if (typeof payload === "string" && payload.includes("<!DOCTYPE html>")) {
        // Backend endpoint doesn't exist - this is a backend implementation issue
        throw new Error(
          "Backend endpoint not implemented. The backend needs to implement POST /api/payments/rent/:rentPaymentId/create-ach-intent. Please contact support or check backend implementation."
        );
      }

      if (typeof payload === "object" && payload) {
        errorMessage = payload.message || payload.error || errorMessage;
      } else if (typeof payload === "string") {
        errorMessage = payload.slice(0, 200);
      }

      // Map HTTP status codes to user-friendly messages
      switch (response.status) {
        case 401:
          errorMessage = "Authentication failed. Please log in again.";
          break;
        case 404:
          // Check if it's the endpoint that doesn't exist
          if (typeof payload === "string" && payload.includes("Cannot POST")) {
            errorMessage =
              "Backend endpoint not implemented. The backend needs to implement POST /api/payments/rent/:rentPaymentId/create-ach-intent. Please contact support.";
          } else {
            errorMessage = "Payment not found. Please contact support.";
          }
          break;
        case 409:
          errorMessage = "This payment has already been processed.";
          break;
        case 422:
          errorMessage =
            "Invalid bank account. Please try a different account.";
          break;
        case 500:
          errorMessage = "Server error. Please try again later.";
          break;
      }

      throw new Error(errorMessage);
    }

    // Return the PaymentIntent with client_secret
    return payload;
  } catch (error) {
    console.error("[createRentPaymentIntentACH] Error:", error);
    throw error;
  }
};

// Get canonical payment status from backend by rentPaymentId
// This is the canonical source of truth (webhook-driven)
export const getRentPaymentStatus = async (rentPaymentId) => {
  try {
    const apiBase = getPaymentApiUrl();
    // Tenant payments don't require authentication
    const authToken = await getAuthToken(false);

    const url = `${apiBase}/payments/rent/${encodeURIComponent(
      rentPaymentId
    )}/status`;
    console.log("[getRentPaymentStatus] Making request to:", url);

    const headers = {
      "Content-Type": "application/json",
    };
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    console.log(
      "[getRentPaymentStatus] Response status:",
      response.status,
      response.statusText
    );

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    console.log("[getRentPaymentStatus] Response payload:", payload);

    if (!response.ok) {
      let errorMessage = "Failed to get payment status";

      if (typeof payload === "object" && payload) {
        errorMessage = payload.message || payload.error || errorMessage;
      } else if (typeof payload === "string") {
        errorMessage = payload.slice(0, 200);
      }

      throw new Error(errorMessage);
    }

    return payload;
  } catch (error) {
    console.error("[getRentPaymentStatus] Error:", error);
    throw error;
  }
};

// Legacy function - kept for backward compatibility
export const getPaymentStatus = async ({ rentPaymentId, paymentIntentId }) => {
  // Prefer rentPaymentId if available
  if (rentPaymentId) {
    return getRentPaymentStatus(rentPaymentId);
  }

  // Fallback to old endpoint if only paymentIntentId is available
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken();

    const params = new URLSearchParams();
    if (paymentIntentId) params.append("paymentIntentId", paymentIntentId);

    const url = `${apiBase}/payments/status?${params.toString()}`;
    console.log("[getPaymentStatus] Making request to:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      let errorMessage = "Failed to get payment status";
      if (typeof payload === "object" && payload) {
        errorMessage = payload.message || payload.error || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return payload;
  } catch (error) {
    console.error("[getPaymentStatus] Error:", error);
    throw error;
  }
};

export const payRentPaymentACH = async ({
  rentPaymentId,
  bankAccountToken,
}) => {
  return createRentPaymentIntentACH({ rentPaymentId, bankAccountToken });
};

// Legacy functions for backward compatibility (deprecated)
export const payInvoiceCard = async ({
  invoiceId,
  paymentMethodId,
  rentPaymentId,
}) => {
  console.warn("payInvoiceCard is deprecated. Use payRentPaymentCard instead.");
  return payRentPaymentCard({ rentPaymentId, paymentMethodId });
};

export const payInvoiceACH = async ({
  invoiceId,
  bankAccountToken,
  rentPaymentId,
}) => {
  console.warn("payInvoiceACH is deprecated. Use payRentPaymentACH instead.");
  return payRentPaymentACH({ rentPaymentId, bankAccountToken });
};

// Save payment method for future use
export const savePaymentMethod = async (paymentMethodId, tenantId) => {
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/save-payment-method`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ paymentMethodId, tenantId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to save payment method");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error saving payment method:", error);
    throw error;
  }
};

// Get payment method details
export const getPaymentMethod = async (paymentMethodId) => {
  try {
    const apiBase = getPaymentApiUrl();
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/payment-method/${paymentMethodId}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get payment method");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting payment method:", error);
    throw error;
  }
};

// Stripe Connect functions for landlord onboarding
export const createConnectAccount = async (
  email,
  landlordId,
  country = "US"
) => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();

    const url = `${apiBase}/frontend-stripe/connect/create-account`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ email, landlordId, country }),
    });

    if (!response.ok) {
      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("text/html")) {
        throw new Error(
          "Backend API endpoint not available. Please implement the backend API endpoints."
        );
      }

      // Clone response to avoid "body stream already read" error
      const responseClone = response.clone();

      try {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create Connect account");
      } catch (jsonError) {
        // If JSON parsing failed, try reading as text from the clone
        try {
          const textResponse = await responseClone.text();
          throw new Error(`Server error (${response.status}): ${textResponse}`);
        } catch (textError) {
          // If that also fails, use a generic error message
          throw new Error(
            `Server error (${response.status}): ${jsonError.message}`
          );
        }
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating Connect account:", error);

    // Provide more helpful error messages
    if (error.message.includes("Failed to fetch")) {
      throw new Error(
        `Unable to connect to payment service at ${apiBase}. Please check if the backend server is running.`
      );
    } else if (error.message.includes("Not a valid URL")) {
      throw new Error(
        `Invalid API URL: ${apiBase}. Please set REACT_APP_API_URL environment variable correctly.`
      );
    }

    throw error;
  }
};

export const createAccountLink = async (accountId, refreshUrl, returnUrl) => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/connect/create-account-link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ accountId, refreshUrl, returnUrl }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create account link");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating account link:", error);
    throw error;
  }
};

export const getConnectAccountStatus = async (accountId) => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/connect/account-status/${accountId}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get account status");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting account status:", error);
    throw error;
  }
};

/**
 * List all Connect accounts for the current user (Primary + linked LLC/entity accounts).
 * Used to populate "Rent deposits to" dropdown. Falls back to primary-only when backend does not implement this.
 * @returns {Promise<{ accounts: Array<{ id, name, stripeConnectId, connectStatus?, isPrimary }> }>}
 */
export const listConnectAccounts = async () => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();
    const response = await fetch(`${apiBase}/connect/accounts`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
      if (response.status === 404 || response.status === 501)
        return { accounts: [] };
      const err = await response.json().catch(() => ({}));
      throw new Error(
        err.error || `Failed to list Connect accounts: ${response.status}`
      );
    }
    return response.json();
  } catch (error) {
    console.warn("listConnectAccounts:", error.message);
    return { accounts: [] };
  }
};

/**
 * Create an additional Connect account (e.g. for an LLC). Backend creates the Stripe account and stores it.
 * Frontend should then call createAccountLink(accountId, refreshUrl, returnUrl) and redirect to the URL.
 * @param {string} name - Display name for the account (e.g. "Sunset LLC")
 * @returns {Promise<{ accountId: string, entityId?: string, onboardingUrl?: string }>}
 */
export const createAdditionalConnectAccount = async (name) => {
  const apiBase = getPaymentApiUrl();
  const authToken = await getAuthToken();
  const response = await fetch(`${apiBase}/connect/accounts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ name: name?.trim() || "Deposit account" }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to create deposit account");
  }
  return response.json();
};

/**
 * Update an LLC/deposit account's display name.
 * @param {string} entityId - Entity document id (from listConnectAccounts)
 * @param {Object} data - { name: string }
 */
export const updateConnectAccount = async (entityId, data) => {
  const apiBase = getPaymentApiUrl();
  const authToken = await getAuthToken();
  const response = await fetch(`${apiBase}/connect/accounts/${entityId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to update deposit account");
  }
  return response.json();
};

/**
 * Remove an LLC/deposit account. Backend may require no properties reference it.
 * @param {string} entityId - Entity document id
 */
export const deleteConnectAccount = async (entityId) => {
  const apiBase = getPaymentApiUrl();
  const authToken = await getAuthToken();
  const response = await fetch(`${apiBase}/connect/accounts/${entityId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to remove deposit account");
  }
  return response.json();
};
// If the account hasn't completed onboarding, the backend will automatically create and return an onboarding link instead
// @param {string} accountId - Stripe Connect account ID
// @param {string} [returnUrl] - URL to redirect to after onboarding/login completes (defaults to /payment-setup?success=true&account=acct_xxx)
// @param {string} [refreshUrl] - URL to redirect to if user needs to refresh/retry (defaults to /payment-setup)
// @returns {Promise<{url: string, isOnboardingLink?: boolean, expires_at?: number, message?: string}>}
export const createLoginLink = async (accountId, returnUrl, refreshUrl) => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();

    // Determine default URLs based on current page context
    const baseUrl = window.location.origin;
    const pathname = window.location.pathname;

    // Set default URLs based on current page context
    // Payment setup completion uses query params matching PaymentSetup page expectations: ?success=true&account=acct_xxx
    let defaultReturnUrl = `${baseUrl}/payment-setup?success=true&account=${accountId}`;
    let defaultRefreshUrl = `${baseUrl}/payment-setup`;

    // If on settings page, use settings URLs with hash anchor
    if (pathname.includes("/settings")) {
      defaultReturnUrl = `${baseUrl}/settings#payment-setup`;
      defaultRefreshUrl = `${baseUrl}/settings#payment-setup`;
    }
    // If on connect page, use connect URLs
    else if (pathname.includes("/connect")) {
      defaultReturnUrl = `${baseUrl}/connect?status=complete`;
      defaultRefreshUrl = `${baseUrl}/connect`;
    }

    // Use provided URLs or fall back to defaults
    const finalReturnUrl = returnUrl || defaultReturnUrl;
    const finalRefreshUrl = refreshUrl || defaultRefreshUrl;

    const response = await fetch(
      `${apiBase}/frontend-stripe/connect/create-login-link`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          accountId,
          returnUrl: finalReturnUrl,
          refreshUrl: finalRefreshUrl,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || errorData.error || "Failed to create login link"
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating login link:", error);
    throw error;
  }
};

// Get all payment methods for a tenant
export const getTenantPaymentMethods = async (tenantId) => {
  const apiBase = getPaymentApiUrl();
  try {
    const authToken = await getAuthToken();
    const response = await fetch(
      `${apiBase}/frontend-stripe/tenant/${tenantId}/payment-methods`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get payment methods");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting payment methods:", error);
    throw error;
  }
};

// Handle webhook events (for backend)
export const handleStripeWebhook = async (event) => {
  try {
    // This would be handled on your backend
    console.log("Webhook event received:", event.type);

    switch (event.type) {
      case "payment_intent.succeeded":
        // Handle successful payment
        console.log("Payment succeeded:", event.data.object);
        break;
      case "payment_intent.payment_failed":
        // Handle failed payment
        console.log("Payment failed:", event.data.object);
        break;
      default:
        console.log("Unhandled event type:", event.type);
    }

    return { received: true };
  } catch (error) {
    console.error("Error handling webhook:", error);
    throw error;
  }
};

/**
 * Check if webhook endpoint exists and is accessible
 * @returns {Promise<{exists: boolean, accessible: boolean, error?: string}>}
 */
export const checkWebhookEndpoint = async () => {
  let baseUrl;
  try {
    baseUrl = getPaymentApiUrl().replace(/\/$/, "");
  } catch {
    return {
      exists: false,
      accessible: false,
      error: "API_URL_NOT_CONFIGURED",
    };
  }

  try {
    // Build URL - if baseUrl already includes /api, don't add it again
    const webhookUrl = baseUrl.includes("/api")
      ? `${baseUrl}/payments/stripe/webhook`
      : `${baseUrl}/api/payments/stripe/webhook`;

    console.log(
      "[checkWebhookEndpoint] Checking webhook endpoint:",
      webhookUrl
    );

    // Try a HEAD request first (if supported), otherwise GET
    try {
      const response = await fetch(webhookUrl, {
        method: "HEAD",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // HEAD request succeeded - endpoint exists
      return {
        exists: true,
        accessible: response.ok || response.status === 405, // 405 = Method Not Allowed (but endpoint exists)
        status: response.status,
      };
    } catch (headError) {
      // HEAD not supported, try GET
      try {
        const response = await fetch(webhookUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        // GET request succeeded - endpoint exists (even if it returns an error for GET)
        return {
          exists: true,
          accessible: response.status !== 404,
          status: response.status,
        };
      } catch (getError) {
        // Both HEAD and GET failed
        if (
          getError.message.includes("404") ||
          getError.message.includes("Not Found")
        ) {
          return {
            exists: false,
            accessible: false,
            error: "ENDPOINT_NOT_FOUND",
          };
        }
        throw getError;
      }
    }
  } catch (error) {
    console.error(
      "[checkWebhookEndpoint] Error checking webhook endpoint:",
      error
    );
    return {
      exists: false,
      accessible: false,
      error: error.message || "UNKNOWN_ERROR",
    };
  }
};
